import React, { useEffect, useState } from 'react';

import { Stack, Text, Title, Group, Divider, Loader } from '@mantine/core';
import dayjs from 'dayjs';
import useAthleteBirthdays from 'hooks/reports/athletes/useAthleteBirthdays';
import { useNavigate } from 'react-router-dom';
import { AthleteBirthdayReport } from 'types';
import { getWeekRangeDates } from 'utils/date-functions';

import { useDashboardStyles } from '../styles';

const AthleteBirthdaysCard = (): JSX.Element => {
  const { classes: dashboard } = useDashboardStyles();
  const navigate = useNavigate();

  const [weekStart, weekEnd] = getWeekRangeDates();
  const [tableData, setTableData] = useState<AthleteBirthdayReport[]>([]);

  const { data, isLoading, isError, isSuccess } = useAthleteBirthdays({
    dateStart: dayjs(weekStart).format('YYYY-MM-DD'),
    dateEnd: dayjs(weekEnd).format('YYYY-MM-DD'),
    ordering: 'dob',
  });

  const handleCardClick = (report: string): void => {
    navigate(`/reports/${report}`);
  };

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  return (
    <div
      className={`${dashboard.birthdays} ${dashboard.card} ${dashboard.clickableCard}`}
      onClick={() => handleCardClick('athlete-birthdays')}
    >
      <div className={dashboard.cardHeader}>
        <Title order={4}>Birthdays this Week</Title>
      </div>

      <Divider />

      {isLoading && (
        <Group position='center' py='xl'>
          <Loader size='lg' />
        </Group>
      )}

      {isError && (
        <Group position='center' py='xl'>
          <Text>Error</Text>
        </Group>
      )}

      {isSuccess && tableData.length > 0 && (
        <Stack p='md'>
          {tableData.map(athlete => {
            if (athlete.dob) {
              const today = dayjs().format('YYYY-MM-DD');
              const birthdayIsToday = athlete.dob.slice(5) === today.slice(5);
              return (
                <Group position='apart' noWrap px='md' key={athlete.athleteId}>
                  <Group>
                    {birthdayIsToday && <Text>&#127882;</Text>}
                    <Text fw='bold' color={birthdayIsToday ? undefined : 'dimmed'}>
                      {athlete.fullName}
                    </Text>
                  </Group>
                  <Text fw='bold' color={birthdayIsToday ? undefined : 'dimmed'}>
                    {dayjs(athlete.dob).format('MMMM D, YYYY')}
                  </Text>
                </Group>
              );
            }
          })}
        </Stack>
      )}

      {isSuccess && tableData.length === 0 && (
        <Group position='center' py='xl'>
          <Text>No birthdays this week</Text>
        </Group>
      )}
    </div>
  );
};

export default AthleteBirthdaysCard;
